<template>
  <div class="amap-location-select">
    <div class="search-wrapper">
      <a-input-search
        v-model:value="searchKeyword"
        placeholder="请输入地点进行搜索"
        enter-button
        @search="handleSearch"
        :loading="searchLoading"
        allow-clear
      />
    </div>
    <div ref="mapRef" class="map-container"></div>
    <div class="info-footer" v-if="locationInfo.address">
      <div class="address-text">
        <environment-outlined />
        <span>{{ locationInfo.address }}</span>
      </div>
      <div class="coords-text">
        经度: {{ locationInfo.lng.toFixed(6) }} | 纬度: {{ locationInfo.lat.toFixed(6) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, onUnmounted, watch, nextTick } from "vue";
import AMapLoader from "@amap/amap-jsapi-loader";
import { EnvironmentOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";

interface LocationInfo {
  lng: number;
  lat: number;
  address: string;
}

const props = defineProps({
  value: {
    type: Object as () => { lng: number; lat: number } | null,
    default: null,
  },
  address: {
    type: String,
    default: "",
  },
  height: {
    type: String,
    default: "400px",
  },
  zoom: {
    type: Number,
    default: 13,
  },
  amapKey: {
    type: String,
    default: "ab312b9438fe581216cdec4aa1cd8da1", // 默认测试Key，实际使用时请替换为申请的Key
  },
  securityCode: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:value", "update:address", "change"]);

const mapRef = ref<HTMLElement | null>(null);
const searchKeyword = ref("");
const searchLoading = ref(false);
const locationInfo = reactive<LocationInfo>({
  lng: 0,
  lat: 0,
  address: "",
});

let map: any = null;
let marker: any = null;
let geocoder: any = null;
let AMapInstance: any = null;

// 初始化地图
const initMap = async () => {
   // 配置安全密钥,自2021年12月02日升级，升级之后所申请的 key 必须配备安全密钥 jscode 一起使用
    (window as any)._AMapSecurityConfig = {
      securityJsCode: '2aa1448afd099bad6b11c2c88c3abd82',
    };

  try {
    AMapInstance = await AMapLoader.load({
      key: 'ab312b9438fe581216cdec4aa1cd8da1',
      version: "2.0",
      plugins: ["AMap.Geocoder", "AMap.AutoComplete", "AMap.PlaceSearch"],
    });

    const initialCenter =
      props.value?.lng && props.value?.lat
        ? [props.value.lng, props.value.lat]
        : [116.397428, 39.90923];

    map = new AMapInstance.Map(mapRef.value, {
      zoom: props.zoom,
      center: initialCenter,
    });

    geocoder = new AMapInstance.Geocoder({
      city: "全国",
    });

    marker = new AMapInstance.Marker({
      position: initialCenter,
      map: map,
      draggable: true,
    });

    // 初始位置解析
    if (props.value?.lng && props.value?.lat) {
      locationInfo.lng = props.value.lng;
      locationInfo.lat = props.value.lat;
      locationInfo.address = props.address;
    }

    // 地图点击事件
    map.on("click", (e: any) => {
      const { lng, lat } = e.lnglat;
      setLocation(lng, lat);
    });

    // 标记拖拽结束事件
    marker.on("dragend", (e: any) => {
      const { lng, lat } = e.lnglat;
      setLocation(lng, lat);
    });
  } catch (error) {
    console.error("AMap initialization failed:", error);
    message.error("地图加载失败，请检查网络或配置");
  }
};

// 设置位置并进行逆地理编码
const setLocation = (lng: number, lat: number, shouldEmit = true) => {
  locationInfo.lng = lng;
  locationInfo.lat = lat;

  const position = new AMapInstance.LngLat(lng, lat);
  marker.setPosition(position);
  map.setCenter(position);

  geocoder.getAddress(position, (status: string, result: any) => {
    if (status === "complete" && result.regeocode) {
      const address = result.regeocode.formattedAddress;
      locationInfo.address = address;

      if (shouldEmit) {
        emit("update:value", { lng, lat });
        emit("update:address", address);
        emit("change", { lng, lat, address });
      }
    } else {
      message.warning("未能解析该位置的详细地址");
    }
  });
};

// 搜索处理
const handleSearch = (value: string) => {
  if (!value) return;
  searchLoading.value = true;

  const placeSearch = new AMapInstance.PlaceSearch({
    city: "全国",
    map: map,
  });

  placeSearch.search(value, (status: string, result: any) => {
    searchLoading.value = false;
    if (status === "complete" && result.poiList && result.poiList.pois.length > 0) {
      const firstPoi = result.poiList.pois[0];
      const { lng, lat } = firstPoi.location;
      setLocation(lng, lat);
    } else {
      message.info("未搜索到相关位置");
    }
  });
};

// 监听外部值变化
watch(
  () => props.value,
  (newVal) => {
    if (newVal && newVal.lng && newVal.lat && map) {
      if (newVal.lng !== locationInfo.lng || newVal.lat !== locationInfo.lat) {
        setLocation(newVal.lng, newVal.lat, false);
      }
    }
  },
  { deep: true }
);

onMounted(() => {
  nextTick(() => {
    initMap();
  });
});

onUnmounted(() => {
  if (map) {
    map.destroy();
  }
});
</script>

<style lang="less" scoped>
.amap-location-select {
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;

  .search-wrapper {
    padding: 10px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
  }

  .map-container {
    width: 100%;
    height: v-bind("props.height");
    min-height: 300px;
  }

  .info-footer {
    padding: 10px 15px;
    background: #fafafa;
    border-top: 1px solid #f0f0f0;

    .address-text {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #333;
      margin-bottom: 4px;

      .anticon {
        color: #ff4d4f;
      }
    }

    .coords-text {
      font-size: 12px;
      color: #999;
      padding-left: 22px;
    }
  }
}
</style>
